import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'chesshatch_fake_friends';

export interface FakeFriendship {
  playerId: string;
  username: string;
  addedAt: number;
}

/**
 * Hook to manage fake friendships stored in localStorage
 * Allows users to "befriend" bot players visually
 */
export function useFakeFriendships() {
  const [fakeFriends, setFakeFriends] = useState<FakeFriendship[]>([]);
  
  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFakeFriends(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Error loading fake friendships:', error);
      setFakeFriends([]);
    }
  }, []);
  
  // Save to localStorage whenever fakeFriends changes
  const saveFriends = useCallback((friends: FakeFriendship[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(friends));
      setFakeFriends(friends);
    } catch (error) {
      console.error('Error saving fake friendships:', error);
    }
  }, []);
  
  /**
   * Add a fake player as a friend
   */
  const addFakeFriend = useCallback((playerId: string, username: string) => {
    setFakeFriends(prev => {
      // Check if already a friend
      if (prev.some(f => f.playerId === playerId)) {
        return prev;
      }
      
      const newFriends = [
        ...prev,
        {
          playerId,
          username,
          addedAt: Date.now()
        }
      ];
      
      saveFriends(newFriends);
      return newFriends;
    });
  }, [saveFriends]);
  
  /**
   * Remove a fake player from friends
   */
  const removeFakeFriend = useCallback((playerId: string) => {
    setFakeFriends(prev => {
      const newFriends = prev.filter(f => f.playerId !== playerId);
      saveFriends(newFriends);
      return newFriends;
    });
  }, [saveFriends]);
  
  /**
   * Check if a player is a fake friend
   */
  const isFakeFriend = useCallback((playerId: string): boolean => {
    return fakeFriends.some(f => f.playerId === playerId);
  }, [fakeFriends]);
  
  /**
   * Get all fake friends
   */
  const getFakeFriends = useCallback((): FakeFriendship[] => {
    return fakeFriends;
  }, [fakeFriends]);
  
  /**
   * Toggle friendship status
   */
  const toggleFakeFriend = useCallback((playerId: string, username: string) => {
    if (isFakeFriend(playerId)) {
      removeFakeFriend(playerId);
      return false;
    } else {
      addFakeFriend(playerId, username);
      return true;
    }
  }, [isFakeFriend, addFakeFriend, removeFakeFriend]);
  
  return {
    fakeFriends,
    addFakeFriend,
    removeFakeFriend,
    isFakeFriend,
    getFakeFriends,
    toggleFakeFriend,
    fakeFriendsCount: fakeFriends.length
  };
}
